import React from 'react'
import {
  Card,
  CardContent,
  Stack,
  Box,
  Typography,
  Chip,
  Avatar,
  useTheme,
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import { getLocation } from 'pages/Jobs/helpers'
import { getSpecialty, getPhysicianProfileImage } from 'pages/Physicians/helpers'
import { formatName, capitalizeFirstLetter } from '../../common/helpers'

export const ListItemPhysician = ({ data, isActive, onClick }) => {
  const theme = useTheme()
  const location = getLocation(capitalizeFirstLetter(data.city), data.state)
  const subspecialty =
    data.subspecialties && data.subspecialties.length > 0 ? data.subspecialties[0].name : undefined
  const specialty =
    data.specialties.length > 0 ? getSpecialty(data.specialties[0].name, subspecialty) : ''
  const name = formatName(data.user.firstName) + ' ' + formatName(data.user.lastName)
  const image = getPhysicianProfileImage(data.user.profileImage)

  return (
    <Card
      onClick={() => onClick(data.id)}
      sx={{
        borderRadius: 1,
        border: isActive
          ? `2px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.divider}`,
        boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.12)' : '0 1px 4px rgba(0,0,0,0.06)',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderColor: theme.palette.primary.light,
          transition: 'all 0.2s ease-in-out',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
        <Stack spacing={0.75}>
          {/* Row 1: Avatar + Name + Candidate Type */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Avatar
              src={image}
              alt={name}
              sx={{ width: 36, height: 36, mr: 1 }}
            />
            <Typography
              variant='subtitle1'
              sx={{
                color: theme.palette.primary.dark,
                fontWeight: 'medium',
                fontSize: { xs: '0.9rem', md: '0.95rem' },
                lineHeight: 1.3,
                flex: 1,
                minWidth: 0,
              }}
            >
              {name}
            </Typography>
            {data.candidateType?.name && (
              <Chip
                label={data.candidateType.name}
                size='small'
                variant='outlined'
                sx={{
                  height: '20px',
                  fontSize: '0.65rem',
                  ml: 1,
                  flexShrink: 0,
                  display: { xs: 'none', md: 'flex' },
                }}
              />
            )}
          </Box>

          {/* Row 2: Specialty · Location */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
              color: theme.palette.text.secondary,
            }}
          >
            {specialty && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <MedicalServicesIcon sx={{ fontSize: '0.85rem' }} color='action' />
                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                  {specialty}
                </Typography>
              </Box>
            )}
            {location && (
              <>
                {specialty && (
                  <Typography variant='body2' sx={{ fontSize: '0.7rem', color: theme.palette.grey[400] }}>
                    ·
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: '0.85rem' }} color='action' />
                  <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                    {location}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Row 3: Specialty chips + Visa chip (desktop only) */}
          {data.specialties?.length > 0 && (
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'center',
              }}
            >
              {data.specialties.slice(0, 2).map(s => (
                <Chip
                  key={s?.id}
                  label={s?.name}
                  size='small'
                  sx={{ height: '20px', fontSize: '0.65rem' }}
                />
              ))}
              {data.specialties.length > 2 && (
                <Chip
                  label={`+${data.specialties.length - 2}`}
                  size='small'
                  variant='outlined'
                  sx={{ height: '20px', fontSize: '0.65rem' }}
                />
              )}
              {data.visas?.length > 0 && (
                <Chip
                  label='Visa'
                  size='small'
                  variant='outlined'
                  color='success'
                  sx={{ height: '20px', fontSize: '0.65rem' }}
                />
              )}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}
